import React, { useState, useEffect } from 'react';
import { Input, Select, Button, Space, Form } from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import type { AgentType } from '../../../types/krybot';
import { DEFAULT_CONTEXT_EXAMPLES, getDefaultContextExample } from '../../../constants/krybotSchemas';

const { Option } = Select;

interface DefaultContextHelperProps {
  value: Record<string, unknown> | string;
  onChange: (value: Record<string, unknown>) => void;
  agentType?: AgentType;
  disabled?: boolean;
}

const ROLE_OPTIONS = [
  { value: 'architect', label: 'Architect' },
  { value: 'operator', label: 'Operator' },
  { value: 'coach', label: 'Coach' },
  { value: 'integrator', label: 'Integrator' },
  { value: 'development', label: 'Development' },
  { value: 'marketfit', label: 'Market Fit' },
];

const ENVIRONMENT_OPTIONS = [
  { value: 'Prime', label: 'Prime' },
  { value: 'Company', label: 'Company' },
];

const DATA_SOURCE_OPTIONS = [
  { value: 'live', label: 'Live' },
  { value: 'mock', label: 'Mock' },
];

const ARTIFACT_LEVEL_OPTIONS = [
  { value: 'artifact', label: 'Artifact' },
  { value: 'tree', label: 'Tree' },
];

export const DefaultContextHelper: React.FC<DefaultContextHelperProps> = ({
  value,
  onChange,
  agentType,
  disabled = false,
}) => {
  // Parse value - can be object or JSON string
  const parseValue = (val: Record<string, unknown> | string): Record<string, unknown> => {
    if (typeof val === 'string') {
      try {
        return JSON.parse(val) || {};
      } catch {
        return {};
      }
    }
    return val || {};
  };

  const [context, setContext] = useState<Record<string, unknown>>(parseValue(value));
  const [customFields, setCustomFields] = useState<Array<{ key: string; value: string }>>([]);
  const [newCustomKey, setNewCustomKey] = useState('');
  const [newCustomValue, setNewCustomValue] = useState('');

  useEffect(() => {
    const parsed = parseValue(value);
    setContext(parsed);
    
    // Extract custom fields (not in predefined fields)
    const predefinedKeys = new Set(['default_role', 'environment', 'data_source', 'artifact_level', 'ui_role']);
    const custom: Array<{ key: string; value: string }> = [];
    Object.entries(parsed).forEach(([key, val]) => {
      if (!predefinedKeys.has(key)) {
        custom.push({ key, value: String(val) });
      }
    });
    setCustomFields(custom);
  }, [value]);

  const updateContext = (updates: Record<string, unknown>) => {
    if (disabled) return;
    const updated = { ...context, ...updates };
    setContext(updated);
    onChange(updated);
  };

  const handlePredefinedFieldChange = (key: string, val: unknown) => {
    updateContext({ [key]: val });
  };

  const handleAddCustom = () => {
    if (disabled || !newCustomKey.trim() || !newCustomValue.trim()) return;
    const key = newCustomKey.trim();
    if (!Object.prototype.hasOwnProperty.call(context, key)) {
      // Try to parse as JSON, otherwise use as string
      let parsedValue: unknown = newCustomValue;
      try {
        parsedValue = JSON.parse(newCustomValue);
      } catch {
        // Keep as string
      }
      updateContext({ [key]: parsedValue });
      setCustomFields([...customFields, { key, value: newCustomValue }]);
      setNewCustomKey('');
      setNewCustomValue('');
    }
  };

  const handleRemoveCustom = (key: string) => {
    if (disabled) return;
    const updated = { ...context };
    delete updated[key];
    setContext(updated);
    setCustomFields(customFields.filter(f => f.key !== key));
    onChange(updated);
  };

  const handleUpdateCustom = (key: string, newValue: string) => {
    if (disabled) return;
    let parsedValue: unknown = newValue;
    try {
      parsedValue = JSON.parse(newValue);
    } catch {
      // Keep as string
    }
    updateContext({ [key]: parsedValue });
    setCustomFields(customFields.map(f => f.key === key ? { key, value: newValue } : f));
  };

  const handleLoadExample = (exampleKey: keyof typeof DEFAULT_CONTEXT_EXAMPLES) => {
    if (disabled) return;
    const example = DEFAULT_CONTEXT_EXAMPLES[exampleKey];
    setContext(example);
    onChange(example);
  };

  const handleLoadSuggested = () => {
    if (disabled) return;
    const suggested = getDefaultContextExample(agentType);
    setContext(suggested);
    onChange(suggested);
  };

  return (
    <div className="default-context-helper">
      <div className="context-predefined">
        <div className="context-section-header">
          <h4>Common Context Fields</h4>
          <Space>
            <Button size="small" onClick={handleLoadSuggested} disabled={disabled}>
              Load Suggested ({agentType || 'default'})
            </Button>
            <Button size="small" onClick={() => handleLoadExample('minimal')} disabled={disabled}>
              Minimal
            </Button>
          </Space>
        </div>
        <div className="context-fields">
          <div className="context-field">
            <label>Default Role</label>
            <Select
              value={context.default_role as string}
              onChange={(val) => handlePredefinedFieldChange('default_role', val)}
              placeholder="Select role"
              disabled={disabled}
              style={{ width: '100%' }}
            >
              {ROLE_OPTIONS.map(opt => (
                <Option key={opt.value} value={opt.value}>{opt.label}</Option>
              ))}
            </Select>
          </div>

          <div className="context-field">
            <label>Environment</label>
            <Select
              value={context.environment as string}
              onChange={(val) => handlePredefinedFieldChange('environment', val)}
              placeholder="Select environment"
              disabled={disabled}
              style={{ width: '100%' }}
            >
              {ENVIRONMENT_OPTIONS.map(opt => (
                <Option key={opt.value} value={opt.value}>{opt.label}</Option>
              ))}
            </Select>
          </div>

          <div className="context-field">
            <label>Data Source</label>
            <Select
              value={context.data_source as string}
              onChange={(val) => handlePredefinedFieldChange('data_source', val)}
              placeholder="Select data source"
              disabled={disabled}
              style={{ width: '100%' }}
            >
              {DATA_SOURCE_OPTIONS.map(opt => (
                <Option key={opt.value} value={opt.value}>{opt.label}</Option>
              ))}
            </Select>
          </div>

          <div className="context-field">
            <label>Artifact Level</label>
            <Select
              value={context.artifact_level as string}
              onChange={(val) => handlePredefinedFieldChange('artifact_level', val)}
              placeholder="Select artifact level"
              disabled={disabled}
              style={{ width: '100%' }}
            >
              {ARTIFACT_LEVEL_OPTIONS.map(opt => (
                <Option key={opt.value} value={opt.value}>{opt.label}</Option>
              ))}
            </Select>
          </div>

          <div className="context-field">
            <label>UI Role</label>
            <Select
              value={context.ui_role as string}
              onChange={(val) => handlePredefinedFieldChange('ui_role', val)}
              placeholder="Select UI role"
              disabled={disabled}
              style={{ width: '100%' }}
            >
              {ROLE_OPTIONS.map(opt => (
                <Option key={opt.value} value={opt.value}>{opt.label}</Option>
              ))}
            </Select>
          </div>
        </div>
      </div>

      <div className="context-custom">
        <h4>Custom Fields</h4>
        <div className="context-add-custom">
          <Input
            placeholder="Field key"
            value={newCustomKey}
            onChange={(e) => setNewCustomKey(e.target.value)}
            disabled={disabled}
            style={{ width: 200, marginRight: 8 }}
          />
          <Input
            placeholder="Field value (JSON or string)"
            value={newCustomValue}
            onChange={(e) => setNewCustomValue(e.target.value)}
            onPressEnter={handleAddCustom}
            disabled={disabled}
            style={{ width: 300, marginRight: 8 }}
          />
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleAddCustom}
            disabled={disabled || !newCustomKey.trim() || !newCustomValue.trim()}
          >
            Add
          </Button>
        </div>
        {customFields.length > 0 && (
          <div className="context-custom-list">
            {customFields.map((field) => (
              <div key={field.key} className="context-custom-item">
                <Input
                  value={field.key}
                  disabled
                  style={{ width: 150, marginRight: 8 }}
                />
                <Input
                  value={field.value}
                  onChange={(e) => handleUpdateCustom(field.key, e.target.value)}
                  placeholder="Value"
                  disabled={disabled}
                  style={{ width: 300, marginRight: 8 }}
                />
                <Button
                  type="text"
                  danger
                  size="small"
                  icon={<DeleteOutlined />}
                  onClick={() => handleRemoveCustom(field.key)}
                  disabled={disabled}
                >
                  Remove
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="context-json-preview">
        <details>
          <summary>JSON Preview</summary>
          <pre>{JSON.stringify(context, null, 2)}</pre>
        </details>
      </div>
    </div>
  );
};

